import React from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  ModalBody,
  ModalHeader,
  Label,
  FormGroup,
  Form,
  Row,
  Col,
} from 'reactstrap';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useDispatch } from 'react-redux';

import { ReactComponent as CloseButtonSvg } from 'assets/images/location/close-button-svg.svg';
import AppButton from 'components/App/Button';
import ToogleButton from 'components/App/ToogleButton';
import { showSuccess } from 'utils';
import administratorApi from 'api/administratorApi';
import { actionResetExtranetAdministratorPassword } from 'store/administrator/actions';

import styles from './ModalEditAccommodationAccount.module.scss';

// Exactly 9 permission items in the screenshot:
const ACCOMMODATION_PERMISSIONS = [
  {
    id: 'roomRates',
    label: 'Giá phòng',
    desc: 'Quản lý số lượng bán tại chỗ nghỉ và tình trạng phòng trống, giá cả cùng chính sách dành cho khách.',
    value: 'roomRates',
  },
  {
    id: 'promotions',
    label: 'Khuyến mãi',
    desc: 'Thiết lập ưu đãi, giảm giá cùng các ưu đãi đặc biệt khác.',
    value: 'promotions',
  },
  {
    id: 'bookings',
    label: 'Đặt phòng',
    desc: 'Tất cả các mảng liên quan đến đơn đặt, bao gồm yêu cầu chỉnh sửa, phí và chức năng nhắn tin cho khách.',
    value: 'bookings',
    children: [
      {
        id: 'viewCreditCardInfo',
        label: 'Xem thông tin thẻ tín dụng',
        value: 'viewCreditCardInfo',
      },
      {
        id: 'viewContactInfo',
        label: 'Xem thông tin liên hệ của khách',
        value: 'viewContactInfo',
      },
    ],
  },
  {
    id: 'accommodation',
    label: 'Chỗ nghỉ',
    desc: 'Chỉnh sửa thông tin về chỗ nghỉ, bao gồm nội dung công khai cho các du khách tiềm năng thấy.',
    value: 'accommodation',
  },
  {
    id: 'mailbox',
    label: 'Hộp thư',
    desc: 'Trả lời tin nhắn với khách hàng đặt chỗ nghỉ và Bookese',
    value: 'mailbox',
  },
  {
    id: 'reviews',
    label: 'Đánh giá',
    desc: 'Theo dõi phản hồi từ khách hàng, xử lý đánh giá và cải thiện chất lượng dịch vụ chỗ nghỉ.',
    value: 'reviews',
  },
  {
    id: 'finance',
    label: 'Tài chính',
    desc: 'Xem và quản lý các tài liệu cùng cài đặt tài chính.',
    value: 'finance',
  },
  {
    id: 'analytics',
    label: 'Phân tích tích',
    desc: 'Truy cập vào dữ liệu hiệu suất kinh doanh và thông tin chuyên sâu.',
    value: 'analytics',
  },
];

const validationSchema = Yup.object().shape({
  fullName: Yup.string()
    .trim()
    .required('Tên không được để trống')
    .max(100, 'Tên không được quá 100 ký tự'),
  password: Yup.string()
    .test(
      'len',
      'Mật khẩu phải dài từ 8 đến 32 ký tự, bao gồm chữ, số và ký tự đặc biệt',
      (val) => {
        if (!val) return true;
        return /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[^a-zA-Z0-9\s]).{8,32}$/.test(val);
      },
    ),
});

const parseAccessRights = (accessRights) => {
  const settings = {
    roomRates: false,
    promotions: false,
    bookings: {
      isParentOn: false,
      viewCreditCardInfo: false,
      viewContactInfo: false,
    },
    accommodation: false,
    mailbox: false,
    reviews: false,
    finance: false,
    analytics: false,
  };

  if (!accessRights) return settings;

  const rights = accessRights.toLowerCase();

  if (rights.includes('giá') || rights.includes('phòng trống')) {
    settings.roomRates = true;
  }
  if (rights.includes('khuyến mãi') || rights.includes('ưu đãi')) {
    settings.promotions = true;
  }
  if (rights.includes('tin nhắn') || rights.includes('hộp thư') || rights.includes('nhắn tin')) {
    settings.mailbox = true;
  }
  if (rights.includes('đặt') || rights.includes('đơn đặt') || rights.includes('đặt phòng')) {
    settings.bookings.isParentOn = true;
  }
  if (rights.includes('thẻ tín dụng') || rights.includes('thẻ')) {
    settings.bookings.isParentOn = true;
    settings.bookings.viewCreditCardInfo = true;
  }
  if (rights.includes('liên hệ')) {
    settings.bookings.isParentOn = true;
    settings.bookings.viewContactInfo = true;
  }
  if (rights.includes('đánh giá')) {
    settings.reviews = true;
  }
  if (rights.includes('chỗ nghỉ')) {
    settings.accommodation = true;
  }
  if (rights.includes('tài chính')) {
    settings.finance = true;
  }
  if (rights.includes('phân tích')) {
    settings.analytics = true;
  }

  return settings;
};

const ALL_PERMISSIONS = {
  roomRates: true,
  promotions: true,
  bookings: {
    isParentOn: true,
    viewCreditCardInfo: true,
    viewContactInfo: true,
  },
  accommodation: true,
  mailbox: true,
  reviews: true,
  finance: true,
  analytics: true,
};

const mapApiPermissionsToUi = (permission) => {
  const settings = {
    roomRates: false,
    promotions: false,
    bookings: {
      isParentOn: false,
      viewCreditCardInfo: false,
      viewContactInfo: false,
    },
    accommodation: false,
    mailbox: false,
    reviews: false,
    finance: false,
    analytics: false,
  };

  if (!permission) return settings;

  settings.roomRates = !!permission.price;
  settings.promotions = !!permission.promotion;

  if (permission.booking) {
    settings.bookings.isParentOn = !!permission.booking.mainInformation;
    settings.bookings.viewCreditCardInfo = !!permission.booking.paymentInformation;
    settings.bookings.viewContactInfo = !!permission.booking.contactInformation;
  }

  settings.accommodation = !!permission.place;
  settings.mailbox = !!permission.mailbox;
  settings.reviews = !!permission.review;
  settings.finance = !!permission.finance;
  settings.analytics = !!permission.analytic;

  return settings;
};

function ModalEditAccommodationAccount(props) {
  const {
    isOpen,
    onToggleModal,
    administratorsDetail,
    placeId,
    // userRoleLabel,
    actionEditAdministrator,
    actionGetAdministratorsList,
  } = props;
  const dispatch = useDispatch();

  const [permissions, setPermissions] = React.useState(() =>
    parseAccessRights(administratorsDetail?.accessRights),
  );
  const [showPassword, setShowPassword] = React.useState(false);
  const [detailData, setDetailData] = React.useState(null);
  const isMainAccount = React.useMemo(() => administratorsDetail?.accountType === 'SINGLE', [administratorsDetail]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      fullName: detailData?.fullName || administratorsDetail?.fullName || '',
      email: detailData?.email || administratorsDetail?.email || '',
      phoneNumber: detailData?.phoneNumber || administratorsDetail?.phoneNumber || '',
      username: detailData?.username || administratorsDetail?.username || '',
      password: '',
    },
    validationSchema,
    onSubmit: (values) => {
      const result = {
        fullName: values.fullName || undefined,
        placeToApply: placeId || administratorsDetail?.place?._id || undefined,
      };

      if (!isMainAccount) {
        result.permission = {
          place: !!permissions.accommodation,
          price: !!permissions.roomRates,
          promotion: !!permissions.promotions,
          mailbox: !!permissions.mailbox,
          review: !!permissions.reviews,
          finance: !!permissions.finance,
          analytic: !!permissions.analytics,
          booking: {
            mainInformation: !!permissions.bookings?.isParentOn,
            paymentInformation: !!permissions.bookings?.viewCreditCardInfo,
            contactInformation: !!permissions.bookings?.viewContactInfo,
          },
        };
      }

      const callBackSuccess = () => {
        validation.resetForm();
        onToggleModal();
        actionGetAdministratorsList?.();
        showSuccess('Cập nhật thông tin tài khoản thành công');
      };

      const handleResetPassword = () => {
        if (values.password) {
          dispatch(
            actionResetExtranetAdministratorPassword({
              id: administratorsDetail?._id,
              result: { password: values.password },
              callBack: callBackSuccess,
            }),
          );
        } else {
          callBackSuccess();
        }
      };

      if (actionEditAdministrator) {
        actionEditAdministrator({
          adminId: administratorsDetail?._id,
          result,
          callBack: handleResetPassword,
        });
      } else {
        handleResetPassword();
      }
    },
  });

  const handleCancel = React.useCallback(() => {
    validation.resetForm();
    onToggleModal();
  }, [onToggleModal, validation]);

  React.useEffect(() => {
    if (isOpen && administratorsDetail?._id) {
      setShowPassword(false);
      administratorApi
        .getExtranetAdministratorDetail({
          id: administratorsDetail._id,
          place: placeId || undefined,
        })
        .then((res) => {
          const detail = res.payload || res.data || {};
          setDetailData(detail);
          if (isMainAccount) {
            setPermissions(ALL_PERMISSIONS);
          } else {
            const permissionUi = mapApiPermissionsToUi(detail.placePermission?.permission);
            setPermissions(permissionUi);
          }
        })
        .catch((err) => {
          console.error('Failed to fetch extranet administrator detail:', err);
          setDetailData(null);
          if (isMainAccount) {
            setPermissions(ALL_PERMISSIONS);
          } else {
            setPermissions(parseAccessRights(administratorsDetail?.accessRights));
          }
        });
    } else {
      setDetailData(null);
    }
  }, [isOpen, administratorsDetail, isMainAccount, placeId]);

  const handleToggleParent = React.useCallback((key, hasChildren) => (e) => {
    const isChecked = e.target.checked;
    setPermissions((prev) => {
      const next = { ...prev };
      if (hasChildren) {
        const childrenKeys = Object.keys(next[key]).filter((k) => k !== 'isParentOn');
        const updatedChildren = {};
        childrenKeys.forEach((childKey) => {
          updatedChildren[childKey] = isChecked ? next[key][childKey] : false;
        });
        next[key] = {
          isParentOn: isChecked,
          ...updatedChildren,
        };
      } else {
        next[key] = isChecked;
      }
      return next;
    });
  }, []);

  const handleToggleChild = React.useCallback((parentKey, childKey) => (e) => {
    const isChecked = e.target.checked;
    setPermissions((prev) => {
      const next = { ...prev };
      const updatedParent = {
        ...next[parentKey],
        [childKey]: isChecked,
      };

      updatedParent.isParentOn = Object.keys(updatedParent)
        .filter((k) => k !== 'isParentOn')
        .some((k) => updatedParent[k]);

      next[parentKey] = updatedParent;
      return next;
    });
  }, []);

  const CloseButton = React.useMemo(() => (
    <button className={styles.ButtonClose} onClick={handleCancel} type="button" aria-label="Close">
      <CloseButtonSvg />
    </button>
  ), [handleCancel]);

  return (
    <div className={styles.Component}>
      <Modal
        fade
        size="lg"
        isOpen={isOpen}
        toggle={handleCancel}
        className={styles.Modal}
        centered
        scrollable
      >
        <ModalHeader
          toggle={handleCancel}
          className={styles.ModalHeader}
          close={CloseButton}
        />

        <ModalBody className={styles.ModalBody}>
          <h2 className={styles.Title}>Chỉnh sửa tài khoản</h2>
          <div className={styles.Divider} />

          <Form onSubmit={validation.handleSubmit} noValidate>
            <div className={styles.FormGroup}>
              <label htmlFor="email" className={styles.FormLabel}>Email</label>
              <input
                id="email"
                name="email"
                type="email"
                placeholder="Nhập Email"
                className={styles.Input}
                disabled
                value={validation.values.email}
              />
            </div>

            <Row>
              <Col md={6}>
                <div className={styles.FormGroup}>
                  <label htmlFor="fullName" className={styles.FormLabel}>Tên</label>
                  <input
                    id="fullName"
                    name="fullName"
                    type="text"
                    placeholder="Nhập tên"
                    className={styles.Input}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.fullName}
                    disabled={!isMainAccount}
                  />
                  {validation.touched.fullName && validation.errors.fullName && (
                    <div className={styles.ErrorMsg}>{validation.errors.fullName}</div>
                  )}
                </div>
              </Col>
              <Col md={6}>
                <div className={styles.FormGroup}>
                  <label htmlFor="phoneNumber" className={styles.FormLabel}>Số điện thoại</label>
                  <input
                    id="phoneNumber"
                    name="phoneNumber"
                    type="text"
                    placeholder="Nhập Số điện thoại"
                    className={styles.Input}
                    disabled
                    value={validation.values.phoneNumber}
                  />
                </div>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <div className={styles.FormGroup}>
                  <label htmlFor="username" className={styles.FormLabel}>Tên đăng nhập</label>
                  <input
                    id="username"
                    name="username"
                    type="text"
                    placeholder="Nhập tên đăng nhập"
                    className={styles.Input}
                    disabled
                    value={validation.values.username}
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className={styles.FormGroup}>
                  <label htmlFor="password" className={styles.FormLabel}>Mật khẩu (để trống nếu không đổi)</label>
                  <div className={styles.PasswordWrapper}>
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Nhập mật khẩu mới"
                      className={`${styles.Input} ${styles.PasswordInput}`}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.password}
                      disabled={!isMainAccount}
                    />
                    <button
                      type="button"
                      className={styles.EyeIconBtn}
                      onClick={() => setShowPassword((prev) => !prev)}
                      aria-label={showPassword ? 'Ẩn mật khẩu' : 'Hiển thị mật khẩu'}
                    >
                      <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`} />
                    </button>
                  </div>
                  {validation.touched.password && validation.errors.password && (
                    <div className={styles.ErrorMsg}>{validation.errors.password}</div>
                  )}
                </div>
              </Col>
            </Row>

            <div className={styles.Divider} style={{ marginTop: '20px', marginBottom: '20px' }} />

            <Label className="Label">
              Người dùng này có thể truy cập những gì?
            </Label>
            <hr className="Divider" />

            {ACCOMMODATION_PERMISSIONS.map((item) => {
              const isChecked = item.children
                ? permissions[item.value]?.isParentOn
                : permissions[item.value];

              return (
                <FormGroup className="SwitchWrapper" key={item.id}>
                  <div className="Parent">
                    <ToogleButton
                      id={item.id}
                      className="Switch"
                      checked={!!isChecked}
                      onChange={handleToggleParent(item.value, !!item.children)}
                      disabled={isMainAccount}
                    />
                    <Label check className="SwitchLabel" htmlFor={item.id}>
                      <span className={styles.spanLable}>{item.label}</span>
                      <span className={styles.desc}>{item.desc}</span>
                    </Label>
                  </div>

                  {item.children && (
                    <div className="Children">
                      {item.children.map((child) => (
                        <FormGroup className="ChildrenWrapper" key={child.id}>
                          <ToogleButton
                            id={child.id}
                            className="Switch"
                            checked={!!permissions[item.value]?.[child.value]}
                            onChange={handleToggleChild(item.value, child.value)}
                            disabled={isMainAccount}
                          />
                          <Label check className="SwitchLabel" htmlFor={child.id}>
                            <span className={styles.spanLable}>{child.label}</span>
                          </Label>
                        </FormGroup>
                      ))}
                    </div>
                  )}
                </FormGroup>
              );
            })}

            <hr className="Divider" />

            <div className="Button__Wrapper">
              <AppButton
                classes="Button Button__Cancel"
                onClick={handleCancel}
                type="button"
              >
                Huỷ
              </AppButton>

              <AppButton classes="Button Button__Invite" type="submit">
                Chỉnh sửa
              </AppButton>
            </div>
          </Form>
        </ModalBody>
      </Modal>
    </div>
  );
}

ModalEditAccommodationAccount.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onToggleModal: PropTypes.func.isRequired,
  administratorsDetail: PropTypes.instanceOf(Object),
  placeId: PropTypes.string,
  // userRoleLabel: PropTypes.string,
  actionEditAdministrator: PropTypes.func,
  actionGetAdministratorsList: PropTypes.func,
};

ModalEditAccommodationAccount.defaultProps = {
  administratorsDetail: {},
  placeId: '',
  // userRoleLabel: '',
  actionEditAdministrator: undefined,
  actionGetAdministratorsList: undefined,
};

export default React.memo(ModalEditAccommodationAccount);
